import CollaborationInput from '../input/index.vue'
import CollaborationSelect from '../select/index.vue'
import CollaborationPhone from '../phone/index.vue'
import { mapActions } from 'vuex'
import validation from '@/mixins/validation'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { phoneNumberValidation, latinOnly, latinOnlyWithSymbols } from '@/mixins/validation'
import { email, maxLength, minLength, required } from 'vuelidate/lib/validators'
import mapKeys from 'lodash/mapKeys'
import snakeCase from 'lodash/snakeCase'
import { trackMeta } from '@/helpers/consent'

const typeOfActivityList = [
  { key: 'private_practice_cosmetologist', title: 'selectPrivatePracticeCosmetologist' },
  { key: 'cosmetologist_injection_nurse', title: 'selectCosmetologistInjectionNurse' },
  { key: 'beauty_salon_studio', title: 'selectBeautySalonStudio' },
  { key: 'network_salon', title: 'selectNetworkSalon' },
  { key: 'beauty_clinic', title: 'selectBeautyClinic' },
  { key: 'network_clinic', title: 'selectNetworkClinic' },
  { key: 'online_store', title: 'selectOnlineStore' },
  { key: 'physical_store', title: 'selectPhysicalStore' },
  { key: 'dealer', title: 'selectDealer' },
  { key: 'masseur', title: 'selectMasseur' },
  { key: 'doctor', title: 'selectDoctor' },
  { key: 'dermatologist', title: 'selectDermatologist' },
  { key: 'aesthetic_medicine_doctor', title: 'selectAestheticMedicineDoctor' },
]

export default {
  name: 'CollaborationForm',
  components: {
    CollaborationInput,
    CollaborationSelect,
    CollaborationPhone,
  },
  mixins: [validation],
  data() {
    return {
      currentLanguage: localStorage.getItem('app_locale'),
      isLoading: false,
      payload: {
        register_mode: 'other',
        email: '',
        name: '',
        lastName: '',
        phone: '',
        workName: '',
        workAddress: '',
        phone_country: '',
        countryCode: null,
        country: null,
        selectedCountry: null,
        city: null,
        selectedCity: null,
        certificateNumber: '',
        workWithVatDisplay: null,
        vatNumber: null,
        icoNumber: '',
        icoConfirmToken: '',
        typeOfActivity: null,
        typeOfActivityList: null,
        password: '',
        registered_from: "spivpracya",
      },
      countries: [],
      filterCountries: [],
      cities: [],
      filterCities: [],
      workWithVatOptions: [
        { text: this.$t('collaborationFormYes'), value: true },
        { text: this.$t('collaborationFormNo'), value: false },
      ],
      workWithVatDisplay: null,
      validationErrors: {},
    }
  },
  watch: {
    'payload.phone'() {
      delete this.validationErrors.phone
    },
    'payload.email'() {
      delete this.validationErrors.email
    },
    'payload.certificateNumber'() {
      delete this.validationErrors.certificateNumber
    },
    'payload.icoNumber'() {
      delete this.validationErrors.icoNumber
    },
    'payload.vatNumber'() {
      delete this.validationErrors.vatNumber
    },
    'payload.workWithVatDisplay'(newVal) {
      if (!newVal) {
        this.payload.vatNumber = null
        delete this.validationErrors.vatNumber
      }
    },
  },
  validations() {
    const validation = {
      payload: {
        name: {
          required,
          latinOnly,
        },
        lastName: {
          required,
          latinOnly,
        },
        phone: {
          required,
          minLength: minLength(8),
        },
        country: {
          required,
        },
        selectedCity: {
          required,
        },
        selectedCountry: {
          required,
        },
        certificateNumber: {
          required,
        },
        email: {
          required,
          maxLength: maxLength(120),
          email,
        },
        workName: {
          latinOnlyWithSymbols,
        },
        workAddress: {
          latinOnlyWithSymbols,
        },
        social: {
          latinOnlyWithSymbols,
        },
        password: {
          required,
          minLength: minLength(8),
        },
      },
    }

    if (this.payload.workWithVatDisplay) {
      validation.payload.vatNumber = {
        required,
      }
    }

    return validation
  },
  async created() {
    try {
      const res = await this.fetchCountries()
      this.countries = res
      this.filterCountries = res
    } catch (error) {
      console.error('Error fetching countries:', error)
    }
  },
  computed: {
    capitalizeWorkingWithVat() {
      const langList = ['cz', 'ru', 'en']
      return !langList.includes(this.currentLanguage)
    },
    countryOptions() {
      return this.filterCountries.map((country) => ({
        value: country.code,
        text: country.name,
      }))
    },
    cityOptions() {
      return this.filterCities.map((city) => ({
        value: city.id,
        text: city.name,
      }))
    },
    typeOfActivityOptions() {
      return typeOfActivityList.map((activity) => ({
        value: activity.key,
        text: this.$t(activity.title),
      }))
    },
    // Errors
    nameErrors() {
      return this.computeErrors('payload', 'name', {
        required: 'validationRequired',
        latinOnly: 'onlyLatinLettersAllowed',
      })
    },
    lastNameErrors() {
      return this.computeErrors('payload', 'lastName', {
        required: 'validationRequired',
        latinOnly: 'onlyLatinLettersAllowed',
      })
    },
    certificateNumberErrors() {
      return this.computeErrors('payload', 'certificateNumber', {
        required: 'validationRequired',
      })
    },
    passwordErrors() {
      return this.computeErrors('payload', 'password', {
        required: 'validationRequired',
        minLength: { message: 'validationMin', count: 8 },
      })
    },
    phoneErrors() {
      return this.computeErrors('payload', 'phone', {
        required: 'validationRequired',
        minLength: { message: 'validationMin', count: 8 },
      })
    },
    countryErrors() {
      return this.computeErrors('payload', 'selectedCountry', {
        required: 'validationRequired',
      })
    },
    cityErrors() {
      return this.computeErrors('payload', 'selectedCity', {
        required: 'validationRequired',
      })
    },
    emailErrors() {
      return this.computeErrors('payload', 'email', {
        maxLength: { message: 'validationMax', count: 120 },
        required: 'validationRequired',
        email: 'validationEmail',
      })
    },
    workNameErrors() {
      return this.computeErrors('payload', 'workName', {
        latinOnlyWithSymbols: 'onlyLatinLettersAllowed',
      })
    },
    workAddressErrors() {
      return this.computeErrors('payload', 'workAddress', {
        latinOnlyWithSymbols: 'onlyLatinLettersAllowed',
      })
    },
    vatNumberErrors() {
      return this.computeErrors('payload', 'vatNumber', {
        required: 'validationRequired',
      })
    },
  },
  methods: {
    ...mapActions({
      registrations: 'auth/REGISTRATION',
      getCountries: 'auth/GET_COUNTRIES',
      fetchTowns: 'order/NOVA_POSHTA_GET_TOWNS',
      fetchCountries: 'country/GET_COUNTRY_LIST',
      fetchCities: 'country/GET_CITY_LIST',
    }),
    async handleCountySelect(e) {
      this.payload.countryCode = e.value
      this.payload.selectedCountry = e.text
      this.payload.country = e.text
      this.payload.phone = ''
      this.payload.city = null
      this.payload.selectedCity = null
      this.$refs.citySelect.reset()
      const res = await this.fetchCities({ code: this.payload.countryCode })
      this.cities = res
      this.filterCities = res
    },
    handleCountyInput(e) {
      this.payload.countryCode = null
      if (e.length < 3) {
        this.filterCountries = this.countries.slice()
        return
      }
      this.filterCountries = this.countries.filter((item) => item.name.toLowerCase().includes(e.toLowerCase()))
    },
    async handleCityInput(e) {
      if (!this.payload.countryCode) return
      if (e.length < 3) {
        const res = await this.fetchCities({ code: this.payload.countryCode })
        this.cities = res
        this.filterCities = res
        return
      }
      const res = await this.fetchCities({ code: this.payload.countryCode, search: e })
      this.cities = res
      this.filterCities = res
    },
    async handleCitySelect(e) {
      this.payload.city = e.text
      this.payload.selectedCity = e.text
    },
    handleTypeOfActivitySelect(e) {
      this.payload.typeOfActivity = e.map((item) => item.title)
      this.payload.typeOfActivityList = e
    },
    handleWorkWithVatSelect(e) {
      this.payload.workWithVatDisplay = e.value
    },
    parsFormData(obj) {
      let fd = new FormData()
      for (let i in obj) {
        if (i === 'type_of_activity_list') {
          fd.append(i, JSON.stringify(obj[i]))
        }
        if (i === 'files') {
          for (let j in obj[i]) {
            fd.append(`files[${j}]`, obj[i][j])
          }
        } else {
          fd.append(i, obj[i])
        }
      }
      return fd
    },

    transformData() {
      let parsedNumber = ''
      this.payload.password = this.payload.password
      this.payload.password_confirmation = this.payload.password
      this.payload.workWithVat = Number(this.payload.workWithVatDisplay)

      try {
        const phone = this.payload.phone
        if (phone) {
          parsedNumber = parsePhoneNumberFromString(phone)
        }
      } catch (error) {
        console.error('Error parsing phone number:', error)
        parsedNumber = ''
      }

      if (parsedNumber) {
        this.payload.phone_country = parsedNumber.country
      } else {
        this.payload.phone_country = ''
      }

      this.payload.confirm_token = this.verifyCodeResponse?.confirmToken
      return mapKeys(this.payload, (value, key) => snakeCase(key))
    },

    handleSuccessRegister() {
      this.payload = {
        register_mode: 'other',
        email: '',
        name: '',
        lastName: '',
        phone: '',
        workName: '',
        workAddress: '',
        phone_country: '',
        countryCode: null,
        country: null,
        selectedCountry: null,
        city: null,
        selectedCity: null,
        certificateNumber: '',
        workWithVatDisplay: null,
        vatNumber: null,
        icoNumber: '',
        icoConfirmToken: '',
        typeOfActivity: null,
        typeOfActivityList: null,
        password: '',
      }

      window.dataLayer.push({
        'event' : 'sign_up',
        'eventCategory' : 'sign_up',
        'eventAction' : 'sign_up',
      });

      trackMeta('sign_up');

      this.$v.$reset()
      this.$refs.citySelect.reset()
      this.$refs.countrySelect.reset()
      this.$refs.typeOfActivitySelect.reset()
      this.$refs.workWithVatSelect.reset()
      this.validationErrors = {}
      this.$toasted.success(this.$t('collaborationFormSuccess'))
    },

    async handleSubmit() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        console.log('Validation errors:', this.$v)
        return
      }
      this.isLoading = true
      try {
        await this.registrations(this.parsFormData(this.transformData()))
        this.handleSuccessRegister()
      } catch (error) {
        if (error.status === 422) {
          this.validationErrors = error.data.errors
        }
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
